import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useDataRequest } from 'hooks/useDataRequest';
import { useIntl } from 'react-intl';
import { DataResult } from 'models/data';
import { Service, useProDataService } from 'services/data.service';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { CategoriesReturnRates } from 'pages/insights-pro/category-tab/components/CategoriesReturnRates';
import { useModelService } from 'services/model/model.service';
import { ReportLinkCard } from 'pages/insights-pro/common/reports/ReportLink';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useEffect } from 'react';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { TopGrowthList } from 'pages/insights-pro/common/components/TopGrowthList';
import { SalesPerformanceList } from 'pages/insights-pro/common/components/SalesPerformanceList';
import { CategoryInfo, CountryInfo, MarketplaceInfo } from './model';
import { TopBottomCategories } from './components/TopBottomCategories';
import { CategoryShares } from './components/CategoryShares';
import { MarketplacesCategoryShares } from './components/MarketplacesCategoryShares';
import { CountriesCategoryShares } from './components/CountriesCategoryShares';
import { CategoryDashboardInfoContent } from './components/CategoryDashboardInfoContent';
import { useCategoryNavigate } from 'pages/insights-pro/category-tab/useCategoryNavigate';

const UNGROUPED_COUNT = 10;

export function CategoryDashboard() {
  const { fetchData } = useProDataService(Service.Categories);
  const intl = useIntl();
  const categoryNavigate = useCategoryNavigate();

  const { getMeasureByMetric, getAttributes } = useModelService();

  const [prdCategory, chnMarketplace, chnCountry] = getAttributes(['prdCategory', 'chnMarketplace', 'chnCountry']);

  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');

  const requestData = useDataRequest();
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const categoryDashboardInfo = {
      titleKey: 'Category Dashboard',
      subtitleKey: 'info.content.all_dates_utc',
      content: <CategoryDashboardInfoContent />,
    };

    setContent(categoryDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [ordGmvVal, setContent]);

  const extractCategoryInfo = (data: DataResult) => {
    const categories: CategoryInfo[] = data.sets.categories.map(entry => {
      const categoryName: string = prdCategory.getValue(entry.attributes);
      return {
        name: categoryName,
        code: prdCategory.getRawValue(entry.attributes),
        color: '',
        entry,
        isOther: false,
      };
    });

    categories.sort((a, b) => ordGmvVal.getValue(b.entry.values) - ordGmvVal.getValue(a.entry.values));

    categories.forEach((_, i) => {
      categories[i].color = getChartColorWithMax(i, UNGROUPED_COUNT);
      if (i >= UNGROUPED_COUNT) {
        categories[i].isOther = true;
      }
    });

    return categories;
  };

  const extractMarketplacesFromCategories = (categories: CategoryInfo[]) => {
    const marketplaces: MarketplaceInfo[] = [];

    categories.forEach(category => {
      category.entry.sets.marketplaces.forEach(categoryMarketplace => {
        const marketplaceName: string = chnMarketplace.getValue(categoryMarketplace.attributes);
        const marketplace =
          marketplaces.find(m => m.name === marketplaceName) ??
          (() => {
            const newMarkeplace = {
              name: marketplaceName,
              totalGmv: 0,
              categories: [],
            };
            marketplaces.push(newMarkeplace);
            return newMarkeplace;
          })();

        marketplace.totalGmv += ordGmvVal.getValue(categoryMarketplace.values);
        marketplace.categories.push({
          ...category,
          entry: categoryMarketplace,
        });
      });
    });
    marketplaces.sort((a, b) => b.totalGmv - a.totalGmv);
    return marketplaces;
  };

  const extractCountriesFromCategories = (categories: CategoryInfo[]) => {
    const countries: CountryInfo[] = [];

    categories.forEach(category => {
      category.entry.sets.countries.forEach(categoryCountry => {
        const countryName: string = chnCountry.getValue(categoryCountry.attributes);
        const country =
          countries.find(m => m.name === countryName) ??
          (() => {
            const newCountry = {
              name: countryName,
              totalGmv: 0,
              categories: [],
            };
            countries.push(newCountry);
            return newCountry;
          })();

        country.totalGmv += ordGmvVal.getValue(categoryCountry.values);
        country.categories.push({ ...category, entry: categoryCountry });
      });
    });
    countries.sort((a, b) => b.totalGmv - a.totalGmv);
    return countries;
  };

  const extractDashboardData = (response: DataResult, isComparisonActive: boolean) => {
    const categories = extractCategoryInfo(response);

    const marketplaces = extractMarketplacesFromCategories(categories);

    const countries = extractCountriesFromCategories(categories);

    return {
      data: response,
      isComparisonActive,
      categories,
      marketplaces,
      countries,
    };
  };

  const dashboardState = useDataServiceQueryResult(
    requestData,
    data => fetchData('dashboard', data),
    response => response,
    ['insights-categories-dashboard']
  );

  if (!dashboardState.data && !dashboardState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const byOrdGmvVal = intl.formatMessage(
    {
      id: 'general.title.by-metric',
    },
    {
      metric: ordGmvVal.metric.name,
    }
  );

  if (dashboardState.isError) {
    return (
      <PageContainer>
        <Tile
          titleId="insights.category"
          errorDetails={dashboardState.error}
          testId="insights-categories-dashboard-error"
        />
      </PageContainer>
    );
  }

  const isComparisonActive = Boolean(
    requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
  );
  const dashboardData = extractDashboardData(dashboardState.data!, isComparisonActive);

  return (
    <PageContainer>
      <Tile
        titleId="insights.category_tab.top_categories_header"
        additionalTitle={byOrdGmvVal}
        className="xl:col-span-6"
      >
        <TopBottomCategories
          categories={dashboardData.categories}
          isComparisonActive={dashboardData.isComparisonActive}
          testId="top-performing-categories"
        />
      </Tile>
      <Tile
        titleId="insights.category_tab.bottom_categories_header"
        additionalTitle={byOrdGmvVal}
        className="xl:col-span-6"
      >
        <TopBottomCategories
          categories={[...dashboardData.categories].reverse()}
          isComparisonActive={dashboardData.isComparisonActive}
          testId="worst-performing-categories"
        />
      </Tile>
      <ReportLinkCard
        titleId="insights.category.reports.channel_breakdown"
        descriptionId="insights.category.reports.channel_breakdown.description"
        linkUrl="reports/channel-breakdown"
        className="xl:col-span-6"
      />
      <ReportLinkCard
        titleId="insights.category.reports.over_time"
        descriptionId="insights.category.reports.over_time.description"
        linkUrl="reports/over-time"
        className="xl:col-span-6"
      />
      <Tile
        className="xl:col-span-6"
        titleId="insights.category_tab.top_growing_header"
        additionalTitle={intl.formatMessage({ id: 'insights.measure.orderedGMVGrowth.by' })}
      >
        <TopGrowthList
          items={dashboardData.categories}
          isComparisonActive={dashboardData.isComparisonActive}
          gmvMeasure={ordGmvVal}
          getNameCellProps={item => ({
            name: item.name,
            icon: findIconForCategory(item.code, 24),
            color: item.color,
          })}
          onClick={item => {
            categoryNavigate(item.code);
          }}
        />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.category_tab.category_return_rates_header"
        additionalTitle={intl.formatMessage({
          id: 'insights.category_tab.category_return_rates_header_subheader',
        })}
      >
        <CategoriesReturnRates
          total={dashboardData.data}
          categories={dashboardData.categories}
          isComparisonActive={dashboardData.isComparisonActive}
        />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.category_tab.category_share_gmv_header"
        additionalTitle={intl.formatMessage(
          {
            id: 'general.title.by-metric',
          },
          {
            metric: ordGmvVal.metric.name,
          }
        )}
      >
        <CategoryShares categories={dashboardData.categories} isComparisonActive={dashboardData.isComparisonActive} />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.products.products_sold_by_categories_header"
        additionalTitle={intl.formatMessage({
          id: 'model.calculated.metric.avgSoldItemsPerProduct.name',
        })}
      >
        <SalesPerformanceList
          attribute={prdCategory}
          isComparisonActive={dashboardData.isComparisonActive}
          entries={dashboardData.categories.map(c => c.entry)}
          getIcon={entry => findIconForCategory(prdCategory.getRawValue(entry.attributes), 24)}
          onClick={categoryNavigate}
        />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.category_tab.category_share_country_header"
        additionalTitle={byOrdGmvVal}
      >
        <CountriesCategoryShares categories={dashboardData.categories} countries={dashboardData.countries} />
      </Tile>
      <Tile
        className="xl:col-span-6"
        titleId="insights.category_tab.category_share_marketplace_header"
        additionalTitle={byOrdGmvVal}
      >
        <MarketplacesCategoryShares categories={dashboardData.categories} marketplaces={dashboardData.marketplaces} />
      </Tile>
    </PageContainer>
  );
}
